'use client'

import { BorderBeam } from '@/components/ui/border-beam'
import {
  signalDayLabel,
  signalModuleLabel,
  useSampleSignals,
} from '@/lib/sampleSignals'

type Severity = 'critical' | 'notable' | 'info'

type Signal = {
  when: string
  severity: Severity
  layer: string
  company: string
  text: string
}

// FALLBACK ONLY (LG-26): static illustrative sample rendered ONLY when
// GET /api/v1/public/sample-signals fails or returns empty, so the hero
// never paints blank. When the endpoint responds, real recent signals
// replace these rows entirely (see useSampleSignals).
//
// Company names here are FICTIONAL on purpose. Pinning specific
// (and easily fact-checkable) pricing / funding / leadership claims on
// real companies reads as us getting their facts wrong, even with a
// "sample" badge. Invented but plausible SaaS names keep the showcase
// realistic without attaching a false claim to a real business.
// One row per signal layer so the sample shows the full BREADTH of what we
// capture and how it maps to severity. Severity leans notable/info (most real
// signals are), with one critical to show the range. Fictional companies, since a
// fallback must never pin a claim on a real company.
const FALLBACK_SIGNALS: Signal[] = [
  { when: 'Today',     severity: 'critical', layer: 'Pricing',     company: 'Northwind',    text: 'Enterprise plan repriced. Per-seat up 18%, annual discount removed.' },
  { when: 'Today',     severity: 'notable',  layer: 'Funding',     company: 'Vertex Labs',  text: 'Series B round announced. New growth-stage lead investor.' },
  { when: 'Today',     severity: 'notable',  layer: 'Product',     company: 'Stratos',      text: 'Background agents now generally available. New tier above Pro.' },
  { when: 'Today',     severity: 'info',     layer: 'Tech stack',  company: 'Beacon',       text: 'Adopted a new edge CDN. Dropped a legacy analytics SDK.' },
  { when: 'Yesterday', severity: 'notable',  layer: 'Leadership',  company: 'Acme Cloud',   text: 'New VP of Product. Joined from a public payments company.' },
  { when: 'Yesterday', severity: 'info',     layer: 'Hiring',      company: 'Tessera',      text: '12 new infrastructure roles posted. SF and remote.' },
  { when: 'Yesterday', severity: 'info',     layer: 'Sentiment',   company: 'Lumen Pay',    text: 'Review sentiment ticking up. Support response time praised.' },
  { when: 'Yesterday', severity: 'info',     layer: 'Positioning', company: 'Halcyon',      text: 'Homepage hero rewritten. Dropped the "work management" framing.' },
]

// Severity colors sourced from tailwind.config.js: maroon (critical),
// amber (notable), olive (info). Kept inline so the BorderBeam can read the
// raw hex; tailwind class versions would round-trip through the JIT compiler.
const DOT: Record<Severity, string> = {
  critical: 'bg-maroon-500 shadow-[0_0_8px_2px_rgba(128,48,60,0.45)]',
  notable:  'bg-amber-500 shadow-[0_0_8px_2px_rgba(255,180,84,0.40)]',
  info:     'bg-olive-500',
}
const SEV_LABEL: Record<Severity, string> = {
  critical: 'Critical',
  notable:  'Notable',
  info:     'Info',
}
const SEV_TEXT: Record<Severity, string> = {
  critical: 'text-maroon-400',
  notable:  'text-amber-400',
  info:     'text-olive-400',
}

export default function HeroSignalFeed() {
  // LG-26: live recent signals are the PRIMARY content; the static sample
  // above is only the no-data fallback.
  const live = useSampleSignals()
  const rows: Signal[] = live
    ? live.slice(0, 7).map((s) => ({
        when: signalDayLabel(s.occurred_at),
        severity: s.severity,
        layer: signalModuleLabel(s.event_type),
        company: s.company_name,
        text: s.summary,
      }))
    : FALLBACK_SIGNALS

  return (
    <div className="relative">
      <div className="card-dark overflow-hidden relative">
        <BorderBeam size={220} duration={9} colorFrom="#f97316" colorTo="#fb923c" />

        <ol className="divide-y divide-white/5" data-signals-source={live ? 'live' : 'fallback'}>
          {rows.map((s, idx) => (
            <li
              key={idx}
              className="flex items-start gap-3 sm:gap-4 px-5 py-3.5"
            >
              <span className="text-[12px] uppercase tracking-widest text-cream-50/55 w-[68px] pt-0.5 shrink-0">
                {s.when}
              </span>
              <span className={'mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ' + DOT[s.severity]} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={'text-[12px] font-semibold uppercase tracking-widest ' + SEV_TEXT[s.severity]}>
                    {SEV_LABEL[s.severity]}
                  </span>
                  <span className="text-[12px] uppercase tracking-widest text-cream-50/55">
                    {s.layer}
                  </span>
                </div>
                <p className="mt-0.5 text-[14px] leading-snug text-cream-50/95">
                  <span className="font-semibold text-cream-50">{s.company}</span>
                  <span className="text-cream-50/55"> · </span>
                  <span className="text-cream-50/85">{s.text}</span>
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      <div className="absolute -z-10 inset-0 blur-3xl opacity-40 pointer-events-none">
        <div className="absolute right-0 top-0 w-1/2 h-full rounded-full bg-terra-500/20" />
      </div>
    </div>
  )
}
